import {
  Firestore,
  doc,
  collection,
  getDocs,
  query,
  orderBy,
  writeBatch,
  runTransaction,
  increment,
  DocumentData
} from 'firebase/firestore';

/**
 * Authoritative, race-safe room operations.
 *
 * These are the operations that MUST be correct under concurrency (multiple
 * clients acting at once, host disconnects, etc.). They are written as pure
 * functions that take a Firestore instance so they can be unit/e2e tested
 * against the emulator with the real security rules and the real transaction
 * semantics. The React hooks call these; the e2e suite mirrors them.
 */

/** Create a room + host player atomically, failing if the code already exists. */
export async function createRoomAtomic(
  db: Firestore,
  roomCode: string,
  roomData: DocumentData,
  hostId: string,
  hostPlayer: DocumentData
): Promise<void> {
  const roomRef = doc(db, 'rooms', roomCode);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef);
    if (snap.exists()) {
      throw new Error('ROOM_CODE_COLLISION');
    }
    tx.set(roomRef, roomData);
    const playerRef = doc(db, 'rooms', roomCode, 'players', hostId);
    tx.set(playerRef, hostPlayer);
  });
}

/**
 * Add a player and bump playerCount with an ATOMIC increment (never a
 * read-modify-write) so concurrent joins can't lose updates.
 */
export async function joinRoomAtomic(
  db: Firestore,
  roomCode: string,
  uid: string,
  player: DocumentData
): Promise<void> {
  const batch = writeBatch(db);
  const playerRef = doc(db, 'rooms', roomCode, 'players', uid);
  batch.set(playerRef, player);
  batch.update(doc(db, 'rooms', roomCode), { playerCount: increment(1) });
  await batch.commit();
}

/** Remove a non-host player and atomically decrement playerCount. */
export async function leaveRoomAsPlayer(
  db: Firestore,
  roomCode: string,
  uid: string
): Promise<void> {
  const batch = writeBatch(db);
  batch.delete(doc(db, 'rooms', roomCode, 'players', uid));
  batch.update(doc(db, 'rooms', roomCode), { playerCount: increment(-1) });
  await batch.commit();
}

/**
 * Idempotently move a round from COLLECTING -> REVEAL, computing winners from
 * the incrementally-maintained tally. Safe to call from multiple clients at
 * once (host at the deadline + fallback clients after a grace period): only the
 * first transaction whose read still sees COLLECTING takes effect.
 *
 * @returns true if THIS call performed the transition, false if it was a no-op.
 */
export async function revealRoundAtomic(
  db: Firestore,
  roomCode: string,
  roundId: string
): Promise<boolean> {
  return runTransaction(db, async (tx) => {
    const roundRef = doc(db, 'rooms', roomCode, 'rounds', roundId);
    const snap = await tx.get(roundRef);
    if (!snap.exists()) return false;
    const data = snap.data();
    if (data.status !== 'COLLECTING') return false; // already revealed by someone

    const tally = (data.tally || {}) as Record<string, number>;
    const counts = Object.values(tally);
    const max = counts.length ? Math.max(...counts) : 0;
    const winners = max > 0 ? Object.keys(tally).filter((id) => tally[id] === max) : [];

    tx.update(roundRef, { status: 'REVEAL', winners });
    return true;
  });
}

/**
 * Deterministically promote `newHostId` to host, but ONLY if `expectedHostId`
 * is still the host. This makes concurrent promotion attempts safe: whoever
 * commits first wins and every other attempt becomes a no-op, guaranteeing
 * exactly one host at all times.
 *
 * @returns true if THIS call performed the transfer.
 */
export async function transferHostAtomic(
  db: Firestore,
  roomCode: string,
  expectedHostId: string,
  newHostId: string
): Promise<boolean> {
  return runTransaction(db, async (tx) => {
    const roomRef = doc(db, 'rooms', roomCode);
    const roomSnap = await tx.get(roomRef);
    if (!roomSnap.exists()) return false;
    if (roomSnap.data().hostId !== expectedHostId) return false; // someone else won

    const newPlayerRef = doc(db, 'rooms', roomCode, 'players', newHostId);
    const newPlayerSnap = await tx.get(newPlayerRef);
    if (!newPlayerSnap.exists()) return false; // successor already gone

    const oldPlayerRef = doc(db, 'rooms', roomCode, 'players', expectedHostId);
    const oldPlayerSnap = await tx.get(oldPlayerRef);

    tx.update(roomRef, { hostId: newHostId });
    if (oldPlayerSnap.exists()) tx.update(oldPlayerRef, { isHost: false });
    tx.update(newPlayerRef, { isHost: true });
    return true;
  });
}

/**
 * Host leaving on purpose while other players remain: transfer host to the
 * earliest-joined remaining player, remove self, and decrement the count — all
 * atomically. Returns the new host id, or null if there was nobody to hand off
 * to (the caller should then delete the room entirely).
 */
export async function leaveRoomAsHost(
  db: Firestore,
  roomCode: string,
  hostId: string
): Promise<string | null> {
  const playersRef = collection(db, 'rooms', roomCode, 'players');
  const snap = await getDocs(query(playersRef, orderBy('joinedAt')));
  const successor = snap.docs.find((d) => d.id !== hostId);
  if (!successor) return null; // host is the last player

  const successorId = successor.id;
  await runTransaction(db, async (tx) => {
    const roomRef = doc(db, 'rooms', roomCode);
    const roomSnap = await tx.get(roomRef);
    if (!roomSnap.exists()) return;
    if (roomSnap.data().hostId !== hostId) return; // already transferred

    const successorRef = doc(db, 'rooms', roomCode, 'players', successorId);
    const successorSnap = await tx.get(successorRef);
    const selfRef = doc(db, 'rooms', roomCode, 'players', hostId);
    const selfSnap = await tx.get(selfRef);
    if (!successorSnap.exists()) return;

    tx.update(roomRef, { hostId: successorId, playerCount: increment(-1) });
    tx.update(successorRef, { isHost: true });
    if (selfSnap.exists()) tx.delete(selfRef);
  });

  return successorId;
}
